/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, FC, useRef, useEffect, useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { PageTitle } from '../../../_metronic/layout/core';
import { KTIcon } from '../../../_metronic/helpers';
import { Dropdown1 } from '../../../_metronic/partials/content/dropdown/Dropdown1';
import {
  RefreshCw, Wifi, WifiOff, Clock,
  Users, Phone, PhoneMissed, CheckCircle,
  AlertCircle, TrendingUp, BarChart3,
  Download, Eye, EyeOff, Maximize2, Minimize2,
  Bell, Settings, Activity, Zap, Target, TrendingDown,
  Star, Award, Battery, BatteryCharging, Headphones,
  MessageSquare, Voicemail, UserCheck, UserX,
  PieChart, LineChart, BarChart, DownloadCloud,
  Shield, ShieldCheck, ShieldAlert, AlertTriangle,
  ChevronUp, ChevronDown, MoreVertical, Filter,
  Play, Pause, SkipForward, SkipBack, Calendar,
  ArrowUpRight, ArrowDownRight, Thermometer,
  Gauge, Timer, Volume2, MessageCircle,
  FileText, Heart, Smile, Frown, Meh,
  LucideIcon
} from 'lucide-react';
import { RealtimeCounts } from './RealtimeCounts';
import CallsPerHour from './CallsPerHour';
import ServiceLevelInbound from "./ServiceLevelInbound";
import AnalyticsPieCard from "./AnalyticsPieCard";
import { Card } from './Card';
import './Styles.css';

// ✅ IMPORT DATE RANGE PICKER
import DateRangePicker from 'react-bootstrap-daterangepicker';
import 'bootstrap-daterangepicker/daterangepicker.css';
import moment from 'moment'

// ==================== TYPES ====================
type MetricColor = 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'purple' | 'cyan';
type AlertType = 'info' | 'warning' | 'danger' | 'success';
type ConnType = 'sse' | 'ws' | 'poll' | 'idle';
type TrendDirection = 'up' | 'down' | 'neutral';
type StatStatus = 'good' | 'warning' | 'neutral';

interface AlertItem {
  type: AlertType;
  message: string;
  icon: LucideIcon;
}

interface StatItem {
  label: string;
  value: string;
  status?: MetricColor;
  icon: LucideIcon;
}

interface PieDataItem {
  name: string;
  value: number;
  color: string;
}

interface SparklineProps {
  data: number[];
  color: string;
  height?: number;
}

interface AnimatedPieCardProps {
  title: string;
  data: PieDataItem[];
  themeColors: Record<string, string>;
  subtitle?: string;
}

interface PremiumMetricCardProps {
  title: string;
  value: number | string;
  subValue?: string;
  icon: LucideIcon;
  color: MetricColor;
  themeColors: Record<string, string>;
  trend?: number;
  sparkline?: number[];
  onClick?: () => void;
  badge?: string;
  suffix?: string;
}

interface ProgressBarProps {
  value: number;
  max?: number;
  color: string;
  label?: string;
  showValue?: boolean;
}

// ==================== UTILITY FUNCTIONS ====================
const fmt = (n: number) => new Intl.NumberFormat('id-ID').format(n);
const fmtPercent = (n: number) => `${Number(n).toFixed(1)}%`;

const fmtDuration = (ms: number): string => {
  if (!ms || ms <= 0) return '00:00';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

// ✅ FORMAT DATE
const formatDate = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// ==================== ANIMATED NUMBER COMPONENT ====================
const AnimatedNumber: FC<{ value: number; duration?: number; prefix?: string; suffix?: string }> = ({ 
  value, duration = 1000, prefix = '', suffix = '' 
}) => {
  const [displayValue, setDisplayValue] = useState(0);
  
  useEffect(() => {
    let startTime: number;
    const startValue = displayValue;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setDisplayValue(Math.floor(startValue + (value - startValue) * easeOut));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [value, duration]);

  return <>{prefix}{fmt(displayValue)}{suffix}</>;
};

// ==================== SPARKLINE MINI CHART ====================
const Sparkline: FC<SparklineProps> = ({ 
  data, color, height = 40 
}) => {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const points = data.map((v, i) => {
    const x = (i / Math.max(data.length - 1, 1)) * 100;
    const y = 100 - ((v - min) / range) * 100;
    return `${x},${y}`;
  }).join(' ');

  const gradientId = `grad-${color.replace(/[^a-zA-Z0-9]/g, '')}`;

  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ height, width: '100%' }}>
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="3"
        points={points}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon
        fill={`url(#${gradientId})`}
        points={`0,100 ${points} 100,100`}
        opacity="0.5"
      />
    </svg>
  );
};

// ==================== TREND INDICATOR ====================
const TrendIndicator: FC<{ value: number; threshold?: number }> = ({ value, threshold = 0 }) => {
  const isPositive = value > threshold;
  const isNeutral = Math.abs(value) <= threshold;
  return (
    <span style={{ 
      display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
      fontSize: '0.75rem', fontWeight: 700,
      color: isNeutral ? '#94a3b8' : isPositive ? '#10b981' : '#ef4444'
    }}>
      {isNeutral ? <Activity size={14} /> : isPositive ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
      {Math.abs(value).toFixed(1)}%
    </span>
  );
};

// ==================== PROGRESS BAR WITH ANIMATION ====================
const AnimatedProgressBar: FC<ProgressBarProps> = ({
  value, max = 100, color, label, showValue = true
}) => {
  const [width, setWidth] = useState(0);
  const percent = Math.min((value / max) * 100, 100);
  
  useEffect(() => {
    const timer = setTimeout(() => setWidth(percent), 100);
    return () => clearTimeout(timer);
  }, [percent]);

  return (
    <div style={{ width: '100%' }}>
      {label && (
        <div style={{ 
          display: 'flex', justifyContent: 'space-between', 
          fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.5rem',
          color: 'inherit'
        }}>
          <span>{label}</span>
          {showValue && <span>{fmtPercent(percent)}</span>}
        </div>
      )}
      <div style={{
        height: '8px', borderRadius: '4px', background: 'rgba(255,255,255,0.15)',
        overflow: 'hidden', position: 'relative'
      }}>
        <div style={{
          width: `${width}%`, height: '100%', borderRadius: '4px',
          background: `linear-gradient(90deg, ${color}, ${color}cc)`,
          transition: 'width 0.8s cubic-bezier(0.34,1.56,0.64,1)',
          position: 'relative'
        }}>
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
            animation: 'shimmer 2s infinite'
          }} />
        </div>
      </div>
    </div>
  );
};

// ==================== ALERT BANNER COMPONENT ====================
const AlertBanner: FC<{ type: AlertType; message: string; icon?: LucideIcon }> = ({
  type, message, icon: Icon
}) => {
  const styles: Record<AlertType, { bg: string; border: string; text: string }> = {
    info: { bg: 'rgba(6,182,212,0.15)', border: 'rgba(6,182,212,0.4)', text: '#06b6d4' },
    warning: { bg: 'rgba(245,158,11,0.15)', border: 'rgba(245,158,11,0.4)', text: '#f59e0b' },
    danger: { bg: 'rgba(239,68,68,0.15)', border: 'rgba(239,68,68,0.4)', text: '#ef4444' },
    success: { bg: 'rgba(16,185,129,0.15)', border: 'rgba(16,185,129,0.4)', text: '#10b981' },
  };
  const style = styles[type];
  
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '0.75rem',
      padding: '0.75rem 1.25rem', borderRadius: '12px',
      background: style.bg, border: `1px solid ${style.border}`,
      color: style.text, fontWeight: 600, fontSize: '0.875rem',
      animation: 'slideInRight 0.3s ease-out'
    }}>
      {Icon && <Icon size={18} />}
      <span>{message}</span>
    </div>
  );
};

// ==================== CUSTOMER INSIGHTS CARD (WITH DARK MODE) ====================
const CustomerInsightsCard: FC<{ data?: any; themeColors: Record<string, string>; isDark: boolean }> = ({ 
  data, themeColors, isDark 
}) => {
  const satisfactionData = useMemo<PieDataItem[]>(() => [
    { name: "Sangat Puas", value: data?.csat_very_satisfied || 0, color: themeColors.success },
    { name: "Puas", value: data?.csat_satisfied || 0, color: "#4ade80" }, 
    { name: "Cukup Puas", value: data?.csat_neutral || 0, color: themeColors.warning },
    { name: "Tidak Puas", value: data?.csat_dissatisfied || 0, color: "#f87171" }, 
    { name: "Sangat Tidak Puas", value: data?.csat_very_dissatisfied || 0, color: themeColors.danger },
  ], [data, themeColors]);

  const csatScore = useMemo(() => {
    return data?.csat_score;
    // const total = (data?.csat_very_satisfied || 70) + (data?.csat_satisfied || 20) + (data?.csat_dissatisfied || 10);
    // return total > 0 ? (((data?.csat_very_satisfied || 70) * 5 + (data?.csat_satisfied || 20) * 3) / total).toFixed(1) : '4.2';
  }, [data]);

  const npsScore = data?.nps_score || 0;
  const fcrRate = data?.first_call_resolution || 0;

  return (
    <div className="customer-insights-card premium-card" style={{
      // background: themeColors.bg,
      borderRadius: '16px',
      padding: '1.25rem',
      // border: `1px solid ${themeColors.border}`,
      // boxShadow: themeColors.cardShadow,
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <div style={{
          width: '32px', height: '32px', borderRadius: '10px',
          background: `${themeColors.purple}20`,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <Heart size={16} style={{ color: themeColors.purple }} />
        </div>
        <div>
          <h4 style={{ 
            margin: 0, fontSize: '0.875rem', fontWeight: 700, 
            letterSpacing: '0.3px'
          }}>Customer Insights</h4>
        </div>
      </div>

      {/* CSAT Score Display - Compact */}
      <div style={{ 
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '0.75rem', marginBottom: '0.75rem',
        // background: `linear-gradient(135deg, ${themeColors.bgSecondary}, ${themeColors.bg})`,
        borderRadius: '12px', border: `1px solid ${themeColors.border}`
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            fontSize: '2rem', fontWeight: 900, 
            background: `linear-gradient(135deg, ${themeColors.success}, ${themeColors.primary})`,
            WebkitBackgroundClip: 'text' as any, WebkitTextFillColor: 'transparent',
            lineHeight: 1
          }}>
            {csatScore}
          </div>
          <div style={{ fontSize: '0.65rem', color: themeColors.textMuted, fontWeight: 600 }}>
            CSAT / 5.0
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.15rem', marginTop: '0.35rem' }}>
            {[1,2,3,4,5].map(star => (
              <Star 
                key={star} 
                size={12} 
                fill={star <= Math.round(Number(csatScore)) ? themeColors.warning : 'none'}
                color={star <= Math.round(Number(csatScore)) ? themeColors.warning : themeColors.textMuted}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Satisfaction Pie Chart - Compact */}
      
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '0.75rem' }}>
        { parseFloat(csatScore?.replace(",", ".")) < 1 ? 
          <AnalyticsPieCard 
            data={satisfactionData}
            title="Satisfaction"
          />
          :''
        }
      </div>
      {/* Key Metrics Grid
      <div style={{ 
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem',
        marginTop: 'auto'
      }}>
        <div style={{
          padding: '0.5rem', borderRadius: '10px',
          background: `${themeColors.success}15`,
          border: `1px solid ${themeColors.success}40`,
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1rem', fontWeight: 800, color: themeColors.success }}>
            {fmtPercent(fcrRate)}
          </div>
          <div style={{ fontSize: '0.6rem', color: themeColors.textMuted, fontWeight: 600 }}>
            FCR
          </div>
        </div>
        <div style={{
          padding: '0.5rem', borderRadius: '10px',
          background: `${themeColors.purple}15`,
          border: `1px solid ${themeColors.purple}40`,
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1rem', fontWeight: 800, color: themeColors.purple }}>
            {npsScore >= 0 ? '+' : ''}{npsScore}
          </div>
          <div style={{ fontSize: '0.6rem', color: themeColors.textMuted, fontWeight: 600 }}>
            NPS
          </div>
        </div>
      </div> */}
    </div>
  );
};

// ==================== SERVICE LEVEL COMPACT (IMPROVED & SMALLER) ====================
const ServiceLevelCompact: FC<{ data?: any; themeColors: Record<string, string>; isDark: boolean }> = ({
  data, themeColors, isDark
}) => {
  const answerPercent = data?.answer_percent || 0;
  const noAnswerPercent = data?.no_answer_percent || 0;
  const inboundAnswer = data?.inbound_answer || 0;
  const inboundNoAnswer = data?.inbound_no_answer || 0;

  return (
    <div className="service-level-compact-card premium-card" style={{
      // background: themeColors.bg,
      borderRadius: '16px',
      padding: '1rem',
      // border: `1px solid ${themeColors.border}`,
      // boxShadow: themeColors.cardShadow,
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <div style={{
          width: '32px', height: '32px', borderRadius: '10px',
          background: `${themeColors.primary}20`,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <Target size={16} style={{ color: themeColors.primary }} />
        </div>
        <div>
          <h4 style={{ 
            margin: 0, fontSize: '0.875rem', fontWeight: 700, 
            letterSpacing: '0.3px'
          }}>Service Level</h4>
        </div>
      </div>

      {/* Dual Gauges - More Compact */}
      <div style={{ 
        // display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1.5rem',
        // flex: 1, marginBottom: '0.75rem'
      }} className='row justify-content-center align-items-center mt-15'>
        {/* Answered */}
        <div style={{ textAlign: 'center' }} className='col-sm-12 col-md-6 col-lg-6'>
          <ServiceLevelInbound 
            value={answerPercent} 
            label="" 
            color={themeColors.success} 
            size={200}
          />
          <div style={{ 
            marginTop: '0.35rem', fontWeight: 800, color: themeColors.success, 
            fontSize: '0.9375rem', lineHeight: 1 
          }}>
            <AnimatedNumber value={inboundAnswer} />
          </div>
          <div style={{ fontSize: '0.7rem', color: themeColors.textMuted, fontWeight: 600 }}>
            Answered
          </div>
        </div>
        
        {/* Missed */}
        <div style={{ textAlign: 'center' }} className='col-sm-12 col-md-6 col-lg-6'>
          <ServiceLevelInbound 
            value={noAnswerPercent} 
            label="" 
            color={themeColors.danger} 
            size={200}
          />
          <div style={{ 
            marginTop: '0.35rem', fontWeight: 800, color: themeColors.danger, 
            fontSize: '0.9375rem', lineHeight: 1 
          }}>
            <AnimatedNumber value={inboundNoAnswer} />
          </div>
          <div style={{ fontSize: '0.7rem', color: themeColors.textMuted, fontWeight: 600 }}>
            Missed
          </div>
        </div>
      </div>

      {/* SLA Progress Bar */}
      <div style={{ 
        padding: '0.5rem', borderRadius: '10px',
        // background: themeColors.bgSecondary,
        marginTop: 'auto'
      }}>
        <div style={{ 
          display: 'flex', justifyContent: 'space-between', 
          fontSize: '0.65rem', fontWeight: 600, marginBottom: '0.3rem',
          // color: themeColors.text 
        }}>
          <span>SLA Compliance</span>
          <span style={{ color: answerPercent >= 80 ? themeColors.success : themeColors.danger }}>
            {fmtPercent(answerPercent)}
          </span>
        </div>
        <AnimatedProgressBar 
          value={answerPercent} 
          max={100} 
          color={answerPercent >= 80 ? themeColors.success : themeColors.danger} 
          showValue={false}
        />
      </div>
    </div>
  );
};

// ==================== PREMIUM METRIC CARD ====================
const PremiumMetricCard: FC<PremiumMetricCardProps> = ({ 
  title, value, subValue, icon: Icon, color, themeColors, trend, sparkline, onClick, badge, suffix 
}) => {
  const colorMap: Record<MetricColor, { bg: string; text: string; glow: string }> = {
    primary: { bg: themeColors.primary, text: '#fff', glow: 'rgba(0,90,170,0.4)' },
    success: { bg: themeColors.success, text: '#fff', glow: 'rgba(16,185,129,0.4)' },
    warning: { bg: themeColors.warning, text: '#fff', glow: 'rgba(245,158,11,0.4)' },
    danger: { bg: themeColors.danger, text: '#fff', glow: 'rgba(239,68,68,0.4)' },
    info: { bg: themeColors.info, text: '#fff', glow: 'rgba(6,182,212,0.4)' },
    purple: { bg: themeColors.purple, text: '#fff', glow: 'rgba(168,85,247,0.4)' },
    cyan: { bg: themeColors.cyan, text: '#fff', glow: 'rgba(6,182,212,0.4)' },
  };
  const colors = colorMap[color];

  const displayValue = typeof value === 'number' 
    ? <AnimatedNumber value={value} suffix={suffix} /> 
    : `${value}${suffix || ''}`;

  return (
    <div 
      className='premium-metric-card'
      onClick={onClick}
      style={{
        background: `linear-gradient(135deg, ${colors.bg} 0%, ${colors.bg}cc 100%)`,
        borderRadius: '16px',
        padding: '1rem 1.25rem',
        color: colors.text,
        cursor: onClick ? 'pointer' : 'default',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
        boxShadow: `0 8px 32px ${colors.glow}`,
        border: `1px solid ${colors.bg}66`,
        backdropFilter: 'blur(10px)'
      }}
    >
      <div style={{
        position: 'absolute', top: '-50%', left: '-50%', width: '200%', height: '200%',
        background: `radial-gradient(circle, ${colors.glow} 0%, transparent 70%)`,
        opacity: 0, transition: 'opacity 0.3s', pointerEvents: 'none'
      }} className='card-glow' />
      
      {badge && (
        <span style={{
          position: 'absolute', top: '0.5rem', right: '0.5rem',
          padding: '0.25rem 0.5rem', borderRadius: '20px',
          background: 'rgba(255,255,255,0.2)', fontSize: '0.625rem', fontWeight: 700
        }}>{badge}</span>
      )}
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
        <div style={{ 
          width: '36px', height: '36px', borderRadius: '10px', 
          background: 'rgba(255,255,255,0.15)', 
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <Icon size={18} />
        </div>
        {trend !== undefined && <TrendIndicator value={trend} />}
      </div>
      
      <div style={{ fontSize: '1.5rem', fontWeight: 800, lineHeight: 1.2, marginBottom: '0.25rem' }}>
        {displayValue}
      </div>
      
      <div style={{ fontSize: '0.75rem', fontWeight: 600, opacity: 0.9, marginBottom: '0.25rem' }}>
        {title}
      </div>
      
      {subValue && (
        <div style={{ fontSize: '0.6875rem', opacity: 0.8 }}>{subValue}</div>
      )}
      
      {sparkline && sparkline.length > 0 && (
        <div style={{ marginTop: '0.75rem', opacity: 0.8 }}>
          <Sparkline data={sparkline} color="rgba(255,255,255,0.8)" height={30} />
        </div>
      )}
    </div>
  );
};

// ==================== STATISTICS DASHBOARD ====================
const StatisticsDashboard: FC<{ data?: any; title?: string }> = ({ data, title = "Performance Statistics" }) => {
  const stats: Record<string, { value: string | number; trend: TrendDirection; change: string; status: StatStatus }> = {
    avgWaitTime: { value: data?.inbound_avg_ms || 0, trend: 'down', change: '8s', status: 'good' },
    avgTalkTime: { value: data?.inbound_aht_ms || 0, trend: 'up', change: '22s', status: 'warning' },
    firstCallResolution: { value: `${data?.first_call_resolution || 0}%`, trend: 'up', change: '2%', status: 'good' },
    customerSatisfaction: { value: `${data?.csat_score || 0}/5`, trend: 'up', change: '0.3', status: 'good' },
    // agentUtilization: { value: `${data?.agent_utilization || 85}%`, trend: 'neutral', change: '0%', status: 'neutral' },
    // occupancyRate: { value: `${data?.occupancy_rate || 92}%`, trend: 'up', change: '5%', status: 'warning' },
    serviceLevel: { value: data?.answer_percent ? `${data.answer_percent}%` : '0%', trend: 'up', change: '2%', status: 'good' },
    abandonRate: { value: data?.abandoned_rate ? `${data?.abandoned_rate}%` : '0%', trend: 'down', change: '1%', status: 'good' }
  };
  
  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return <ArrowUpRight size={12} />;
      case 'down': return <ArrowDownRight size={12} />;
      default: return <span style={{ opacity: 0.5 }}>─</span>;
    }
  };
  
  const getIconForStat = (index: number) => {
    const icons = [Timer, Phone, Target, Star, Users, Activity, Gauge, PhoneMissed];
    const Icon = icons[index] || Activity;
    return <Icon size={16} />;
  };

  return (
    <div className="statistics-dashboard-card premium-card">
      <div className="premium-header">
        <div className="header-left">
          <div className="header-icon-glow">
            <BarChart3 size={24} />
          </div>
          <div>
            <h3 className="card-title-glow">{title}</h3>
            <p className="card-subtitle">Key metrics dashboard</p>
          </div>
        </div>
        {/* <div className="statistics-controls">
          <button className="stat-btn active">Today</button>
          <button className="stat-btn">Week</button>
          <button className="stat-btn">Month</button>
        </div> */}
      </div>
      <div className="card-body">
        <div className="statistics-grid">
          {Object.entries(stats).map(([key, stat], index) => (
            <div key={key} className={`stat-card ${stat.status}`}>
              <div className="stat-card-header">
                <div className="stat-icon">
                  {getIconForStat(index)}
                </div>
                {/* <div className="stat-trend-indicator">
                  {getTrendIcon(stat.trend)}
                  <span className={`trend-text ${stat.trend}`}>
                    {stat.change}
                  </span>
                </div> */}
              </div>
              <div className="stat-card-body">
                <div className="stat-value">{stat.value}</div>
                <div className="stat-label">
                  {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                </div>
              </div>
              <div className="stat-card-glow"></div>
            </div>
          ))}
        </div>
        {/* <div className="statistics-summary">
          <div className="summary-item positive">
            <CheckCircle size={14} />
            <span>7 metrics improving</span>
          </div>
          <div className="summary-item warning">
            <AlertCircle size={14} />
            <span>2 metrics need attention</span>
          </div>
          <div className="summary-item">
            <TrendingUp size={14} />
            <span>Overall performance: +12%</span>
          </div>
        </div> */}
      </div>
    </div>
  );
};

// ==================== ABANDONED ANALYSIS ====================
const AbandonedAnalysis: FC<{ data?: any }> = ({ data }) => {
  const abandonDetails = {
    early: {
      count: data?.ab_Early || 0,
      percentage: data?.ab_pctEarly || 0,
    },
    agent: {
      count: data?.ab_Agent || 0,
      percentage: data?.ab_pctAgent || 0,
    },
    ivr: {
      count: data?.ab_Ivr || 0,
      percentage: data?.ab_pctIvr || 0,
    }
  };
  
  return (
    <div className="abandoned-analysis-card premium-card">
      <div className="premium-header">
        <div className="header-left">
          <div className="header-icon-glow danger">
            <PhoneMissed size={24} />
          </div>
          <div>
            <h3 className="card-title-glow">Abandoned Call Analysis</h3>
            <p className="card-subtitle">Summary overview</p>
          </div>
        </div>
      </div>
      <div className="card-body">
        <div className="abandon-summary-grid">
          <div className="summary-card early">
            <div className="summary-header">
              <span className="summary-title">Early Abandon</span>
              <span className="summary-percentage">{abandonDetails.early.percentage}%</span>
            </div>
            <div className="summary-value">{abandonDetails.early.count}</div>
            <div className="summary-trend">
              {/* <TrendingDown size={14} />
              <span>+12% from yesterday</span> */}
            </div>
            <div className="summary-detail">
              {/* <span>Avg Wait: 2m15s</span>
              <span>Peak: 10:00-11:00</span> */}
            </div>
          </div>
          <div className="summary-card agent">
            <div className="summary-header">
              <span className="summary-title">Agent Abandon</span>
              <span className="summary-percentage">{abandonDetails.agent.percentage}%</span>
            </div>
            <div className="summary-value">{abandonDetails.agent.count}</div>
            <div className="summary-trend">
              {/* <TrendingUp size={14} />
              <span>-3% from yesterday</span> */}
            </div>
            <div className="summary-detail">
              {/* <span>Most: Transfer Failed</span>
              <span>Avg: 4.2 calls/day</span> */}
            </div>
          </div>
          <div className="summary-card ivr">
            <div className="summary-header">
              <span className="summary-title">IVR Abandon</span>
              <span className="summary-percentage">{abandonDetails.ivr.percentage}%</span>
            </div>
            <div className="summary-value">{abandonDetails.ivr.count}</div>
            <div className="summary-trend">
              {/* <TrendingUp size={14} /> */}
              {/* <span>No change</span> */}
            </div>
            <div className="summary-detail">
              {/* <span>Main Menu: 55%</span>
              <span>Tech Issues: 11%</span> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== LIVE QUEUE STATUS ====================
const LiveQueueStatus: FC<{ data?: any }> = ({ data }) => {
  return (
    <div className="live-queue-status-card premium-card">
      <div className="card-header">
        <div className="header-left">
          <div className="header-icon-glow">
            <Users size={24} />
          </div>
          <div>
            <h3 className="card-title">Live Queue Status</h3>
            <p className="card-subtitle">Real-time call center metrics</p>
          </div>
        </div>
        <div className="header-right">
          <div className="queue-counters">
            <div className="counter-item">
              <span className="counter-label">WAITING</span>
              <span className="counter-value">{data?.waiting_calls || 0}</span>
            </div>
            <div className="counter-item">
              <span className="counter-label">ACTIVE AGENTS</span>
              <span className="counter-value">{data?.active_agents || 0}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="card-body">
        <div className="metrics-grid-compact">
          <div className="metric-item">
            <div className="metric-icon warning">
              <Clock size={20} />
            </div>
            <div className="metric-content">
              <div className="metric-value">{data?.waiting_calls || 0}</div>
              <div className="metric-label">Waiting</div>
            </div>
          </div>
          <div className="metric-item">
            <div className="metric-icon success">
              <Users size={20} />
            </div>
            <div className="metric-content">
              <div className="metric-value">{data?.active_agents || 0}</div>
              <div className="metric-label">Active Agents</div>
            </div>
          </div>
          <div className="metric-item">
            <div className="metric-icon primary">
              <Phone size={20} />
            </div>
            <div className="metric-content">
              <div className="metric-value">{data?.inbound_call || 0}</div>
              <div className="metric-label">Inbound Call</div>
            </div>
          </div>
          <div className="metric-item">
            <div className="metric-icon success">
              <CheckCircle size={20} />
            </div>
            <div className="metric-content">
              <div className="metric-value">{data?.inbound_answer || 0}</div>
              <div className="metric-label">Answer Call</div>
            </div>
          </div>
          <div className="metric-item">
            <div className="metric-icon danger">
              <PhoneMissed size={20} />
            </div>
            <div className="metric-content">
              <div className="metric-value">{data?.inbound_no_answer || 0}</div>
              <div className="metric-label">No Answer</div>
            </div>
          </div>
          <div className="metric-item">
            <div className="metric-icon warning">
              <Clock size={20} />
            </div>
            <div className="metric-content">
              <div className="metric-value">{data?.queue_waiting || 0}</div>
              <div className="metric-label">Call Queue</div>
            </div>
          </div>
          <div className="metric-item">
            <div className="metric-icon info">
              <Timer size={20} />
            </div>
            <div className="metric-content">
              <div className="metric-value">{data?.inbound_avg_wait || 0}</div>
              <div className="metric-label">Avg Wait Time</div>
            </div>
          </div>
          <div className="metric-item">
            <div className="metric-icon primary">
              <Phone size={20} />
            </div>
            <div className="metric-content">
              <div className="metric-value">{data?.outgoing_calls || 0}</div>
              <div className="metric-label">Outgoing Call</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== ACTIVITY METRICS ====================
const ActivityMetrics: FC<{ data?: any }> = ({ data }) => {
  return (
    <div className="activity-metrics-card premium-card">
      <div className="card-header">
        <div className="header-left">
          <div className="header-icon-glow">
            <Activity size={24} />
          </div>
          <div>
            <h3 className="card-title">Activity Metrics</h3>
            <p className="card-subtitle">Real-time activity monitoring</p>
          </div>
        </div>
      </div>
      <div className="card-body">
        <div className="metrics-grid-compact">
          <div className="metric-item">
            <div className="metric-icon success">
              <Phone size={20} />
            </div>
            <div className="metric-content">
              <div className="metric-value">{data?.active_calls || 0}</div>
              <div className="metric-label">Active Call</div>
            </div>
          </div>
          <div className="metric-item">
            <div className="metric-icon warning">
              <Phone size={20} />
            </div>
            <div className="metric-content">
              <div className="metric-value">{data?.ringing_calls || 0}</div>
              <div className="metric-label">Ringing</div>
            </div>
          </div>
          <div className="metric-item">
            <div className="metric-icon primary">
              <Phone size={20} />
            </div>
            <div className="metric-content">
              <div className="metric-value">{data?.talking_calls || 0}</div>
              <div className="metric-label">Talking</div>
            </div>
          </div>
          <div className="metric-item">
            <div className="metric-icon primary">
              <Phone size={20} />
            </div>
            <div className="metric-content">
              <div className="metric-value">{data?.incoming_calls || 0}</div>
              <div className="metric-label">Incoming</div>
            </div>
          </div>
          <div className="metric-item">
            <div className="metric-icon info">
              <Headphones size={20} />
            </div>
            <div className="metric-content">
              <div className="metric-value">{data?.ivr_calls || 0}</div>
              <div className="metric-label">IVR</div>
            </div>
          </div>
          <div className="metric-item">
            <div className="metric-icon warning">
              <Users size={20} />
            </div>
            <div className="metric-content">
              <div className="metric-value">{data?.queue_waiting || 0}</div>
              <div className="metric-label">Queue</div>
            </div>
          </div>
          <div className="metric-item">
            <div className="metric-icon primary">
              <Phone size={20} />
            </div>
            <div className="metric-content">
              <div className="metric-value">{data?.outgoing_calls || 0}</div>
              <div className="metric-label">Outgoing</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== QUICK STATS ====================
const QuickStats: FC<{ data?: any; type?: 'inbound' | 'outbound' }> = ({ data, type = 'inbound' }) => {
  const isInbound = type === 'inbound';
  return (
    <div className="quick-stats-card premium-card">
      <div className="card-header">
        <div className="header-left">
          <div className="header-icon-glow">
            <BarChart3 size={24} />
          </div>
          <div>
            <h3 className="card-title">{isInbound ? 'Inbound Quick Stats' : 'Outbound Quick Stats'}</h3>
            <p className="card-subtitle">Key performance indicators</p>
          </div>
        </div>
      </div>
      <div className="card-body">
        <div className="quick-stats-grid">
          <div className="quick-stat-item">
            <div className="quick-stat-icon primary">
              <Phone size={20} />
            </div>
            <div className="quick-stat-content">
              <div className="quick-stat-value">{fmt(isInbound ? (data?.inbound_call || 0) : (data?.outgoing_calls || 0))}</div>
              <div className="quick-stat-label">{isInbound ? 'Inbound Calls' : 'Outbound Calls'}</div>
            </div>
          </div>
          {/* <div className="quick-stat-item">
            <div className="quick-stat-icon primary">
              <PhoneMissed size={20} />
            </div>
            <div className="quick-stat-content">
              <div className="quick-stat-value">{fmt(isInbound ? (data?.outgoing_call || 0) : (data?.outgoing_calls || 0))}</div>
              <div className="quick-stat-label">{isInbound ? 'Outgoing Calls' : 'Total Dialed'}</div>
            </div>
          </div> */}
          <div className="quick-stat-item">
            <div className="quick-stat-icon success">
              <CheckCircle size={20} />
            </div>
            <div className="quick-stat-content">
              <div className="quick-stat-value">{fmt(isInbound ? (data?.inbound_answer || 0) : (data?.connected_calls || 0))}</div>
              <div className="quick-stat-label">{isInbound ? 'Answered' : 'Connected'}</div>
            </div>
          </div>
          <div className="quick-stat-item">
            <div className="quick-stat-icon danger">
              <UserX size={20} />
            </div>
            <div className="quick-stat-content">
              <div className="quick-stat-value">{fmt(isInbound ? (data?.inbound_no_answer || 0) : (data?.failed_calls || 0))}</div>
              <div className="quick-stat-label">{isInbound ? 'No Answer' : 'Failed'}</div>
            </div>
          </div>
          <div className="quick-stat-item">
            <div className="quick-stat-icon warning">
              <Headphones size={20} />
            </div>
            <div className="quick-stat-content">
              <div className="quick-stat-value">{fmt(isInbound ? (data?.agent_ready || 0) : (data?.agent_ready || 0))}</div>
              <div className="quick-stat-label">{isInbound ? 'Agents Ready' : 'Busy'}</div>
            </div>
          </div>
          <div className="quick-stat-item">
            <div className="quick-stat-icon info">
              <Timer size={20} />
            </div>
            <div className="quick-stat-content">
              <div className="quick-stat-value">{fmt(isInbound ? (data?.inbound_avg_wait || 0) : (data?.inbound_avg_wait || 0))}</div>
              <div className="quick-stat-label">{isInbound ? 'Avg Wait Time' : 'Total Duration'}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== ACTIVITIES ====================
const Activities: FC<{ data?: any; type?: 'inbound' | 'outbound' }> = ({ data, type = 'inbound' }) => {
  const [now, setNow] = useState(Date.now());
  // const [filter, setFilter] = useState('all');
  
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  
  const formatDuration = (created: string) => {
    const diff = Math.floor((now - new Date(created).getTime()) / 1000);
    const h = String(Math.floor(diff / 3600)).padStart(2, "0");
    const m = String(Math.floor((diff % 3600) / 60)).padStart(2, "0");
    const s = String(diff % 60).padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  const allCalls = [
    ...(data?.list_inbound_calls || []).map((i: any) => ({ ...i, type: 'inbound' })),
    ...(data?.list_outbound_calls || []).map((i: any) => ({ ...i, type: 'outbound' }))
  ];
  const filteredActivities = allCalls.filter(item => item.type === type);
  // const filteredActivities = data?.list_calls?.filter((item: any) => {
  //   if (filter === 'all') return true;
  //   return true;
  // }) || [];
  
  return (
    <div className="activities-card premium-card">
      <div className="card-header">
        <div className="header-left">
          <div className="header-icon-glow">
            <Activity size={24} />
          </div>
          <div>
            <h3 className="card-title">{type === 'inbound' ? 'Inbound Activities' : 'Outbound Activities'}</h3>
            <p className="card-subtitle">Recent call center activities</p>
          </div>
        </div>
        {/* <div className="header-right">
          <div className="activity-filters">
            <button
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            <button
              className={`filter-btn ${filter === 'incoming' ? 'active' : ''}`}
              onClick={() => setFilter('incoming')}
            >
              Incoming
            </button>
            <button
              className={`filter-btn ${filter === 'outgoing' ? 'active' : ''}`}
              onClick={() => setFilter('outgoing')}
            >
              Outgoing
            </button>
          </div>
        </div> */}
      </div>
      <div className="card-body">
        <div className="activities-timeline">
          {filteredActivities.length > 0 ? (
            filteredActivities.map((item: any, index: number) => (
              <div key={item.id || index} className="timeline-item">
                <div className="timeline-time">
                  {formatDuration(item.created)}
                </div>
                <div className="timeline-content">
                  <div className="timeline-message">{item.message}</div>
                </div>
              </div>
            ))
          ) : (
            <div className="no-activities">
              <AlertCircle size={24} />
              <span>No activities to display</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== MAIN DASHBOARD COMPONENT ====================
const DashboardPage: FC = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const intl = useIntl();
  const endpoint = `${apiUrl}/dashboard/getcount`;
  const pollInterval = 3000;
  
  function getApiToken() {
    try {
      const raw = localStorage.getItem('kt-auth-react-v');
      return raw ? JSON.parse(raw).api_token || '' : '';
    } catch {
      return '';
    }
  }
  
  const [data, setData] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'connecting'>('connecting');
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);
  
  const connectionRef = useRef<{ type: ConnType; ok: boolean }>({ type: 'idle', ok: false });
  const esRef = useRef<EventSource | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const pollRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectDelay = useRef(1000);

  // ✅ State untuk Calls Per Hour Date Range
  const [callsPerHourDateRange, setCallsPerHourDateRange] = useState<[Date | null, Date | null]>([null, null]);

  // Theme Colors - WITH DARK MODE SUPPORT
  const themeColors = useMemo(() => {
    const isDark = document.documentElement.classList.contains('dark-mode');
    return {
      bg: isDark ? '#0f172a' : '#ffffff',
      bgSecondary: isDark ? '#1e293b' : '#f8fafc',
      text: isDark ? '#f1f5f9' : '#1e293b',
      textMuted: isDark ? '#94a3b8' : '#64748b',
      border: isDark ? '#334155' : '#e2e8f0',
      primary: '#005AAA', success: '#10b981', warning: '#f59e0b',
      danger: '#ef4444', info: '#06b6d4', purple: '#a855f7', cyan: '#06b6d4',
      cardShadow: isDark ? '0 8px 32px rgba(0,0,0,0.4)' : '0 8px 32px rgba(30,58,138,0.12)',
    };
  }, []);

  const appendToken = (url: string) => {
    const t = getApiToken();
    if (!t) return url;
    return `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(t)}`;
  };
  
  const applyPayload = (p: any) => {
    const d = typeof p === 'string' ? JSON.parse(p) : p;
    setData(prev => {
      const newData = { ...prev, ...d };
      setLastUpdate(new Date());
      setConnectionStatus('connected');
      return newData;
    });
  };
  
  const cleanup = () => {
    esRef.current?.close();
    wsRef.current?.close();
    if (pollRef.current) clearInterval(pollRef.current);
    esRef.current = wsRef.current = pollRef.current = null;
  };
  
  const getDynamicEndpoint = () => {
    let url = endpoint;
    if (callsPerHourDateRange[0] && callsPerHourDateRange[1]) {
      const start = moment(callsPerHourDateRange[0]).format('YYYY-MM-DD');
      const end = moment(callsPerHourDateRange[1]).format('YYYY-MM-DD');
      url += `${url.includes('?') ? '&' : '?'}call_per_hour_start=${start}&call_per_hour_end=${end}`;
    }
    return url;
  };

  const startPolling = () => {
    connectionRef.current = { type: 'poll', ok: false };
    const run = async () => {
      try {
        const r = await fetch(appendToken(getDynamicEndpoint()), {
          headers: { Authorization: `Bearer ${getApiToken()}` },
        });
        const j = await r.json();
        if (j.success) {
          applyPayload(j.data);
          connectionRef.current.ok = true;
          setConnectionStatus('connected');
        }
      } catch {
        connectionRef.current.ok = false;
        setConnectionStatus('disconnected');
      }
    };
    run();
    pollRef.current = setInterval(run, pollInterval);
  };
  
  const connect = () => {
    cleanup();
    const sseUrl = appendToken(getDynamicEndpoint());
    setConnectionStatus('connecting');
    if ('EventSource' in window) {
      try {
        const es = new EventSource(sseUrl);
        esRef.current = es;
        connectionRef.current = { type: 'sse', ok: false };
        es.onopen = () => {
          connectionRef.current.ok = true;
          setConnectionStatus('connected');
        };
        es.onmessage = e => applyPayload(e.data);
        es.onerror = () => {
          connectionRef.current.ok = false;
          setConnectionStatus('disconnected');
          cleanup();
        };
        return;
      } catch { }
    }
    startPolling();
  };
  
  useEffect(() => {
    if (autoRefresh) connect();
    const timer = setInterval(() => {
      if (autoRefresh && !connectionRef.current.ok) {
        reconnectDelay.current = Math.min(reconnectDelay.current * 1.5, 30000);
        setTimeout(connect, reconnectDelay.current);
      }
    }, 5000);
    return () => { cleanup(); clearInterval(timer); };
  }, [endpoint, autoRefresh, callsPerHourDateRange]);

  // ✅ Set default date range to today
  useEffect(() => {
    if (!callsPerHourDateRange[0] || !callsPerHourDateRange[1]) {
      const today = new Date();
      setCallsPerHourDateRange([today, today]);
    }
  }, []);
  
  const handleRefresh = () => {
    setLoading(true);
    connect();
    setTimeout(() => setLoading(false), 1000);
  };
  
  const formatTimeAgo = (date: Date) => {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  const isDark = document.documentElement.classList.contains('dark-mode');
  
  // ✅ Handler untuk Date Range Change
  const handleCallsPerHourDateChange = ([start, end]: [Date | null, Date | null]) => {
    setCallsPerHourDateRange([start, end]);
  };

  return (
    <div className="dashboard-premium-container">
      {/* Premium Header */}
      <div className="premium-header-gradient">
        <div className="header-content">
          <div className="header-left">
            <div className="dashboard-title-section">
              <div className="title-icon-glow">
                <Activity size={28} />
              </div>
              <div>
                <h1 className="dashboard-title-premium">Call Center Dashboard</h1>
                <p className="dashboard-subtitle-premium">
                  Real-time monitoring & analytics for optimal performance
                </p>
              </div>
            </div>
            <div className="connection-status-premium">
              <div className={`status-indicator ${connectionStatus}`}>
                <div className="status-dot"></div>
                <span className="status-text">
                  {connectionStatus === 'connected' ? 'LIVE CONNECTED' :
                    connectionStatus === 'connecting' ? 'CONNECTING...' : 'DISCONNECTED'}
                </span>
              </div>
              <div className="last-update-premium">
                <Clock size={14} />
                <span>Updated {formatTimeAgo(lastUpdate)}</span>
              </div>
            </div>
          </div>
          <div className="header-right">
            <div className="header-actions">
              <button
                className={`refresh-btn-premium ${loading ? 'loading' : ''}`}
                onClick={handleRefresh}
                disabled={loading}
              >
                <RefreshCw size={18} className={loading ? 'spinning' : ''} />
                <span>Refresh</span>
              </button>
              <div className="auto-refresh-toggle">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">Auto Refresh</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== ROW 1: SERVICE LEVEL + CUSTOMER INSIGHTS (STACKED LAYOUT) ===== */}
      <div className="row g-4 mb-6"> {/* g-4 untuk gap antar card, mb-6 untuk margin bawah */}
        
        {/* Card Atas: Service Level */}
        <div className="col-sm-12 col-md-6 col-lg-6">
          <div className="metric-card-premium h-100">
            <ServiceLevelCompact data={data} themeColors={themeColors} isDark={isDark} />
          </div>
        </div>
        
        {/* Card Bawah: Customer Insights */}
        <div className="col-sm-12 col-md-6 col-lg-6">
          <div className="metric-card-premium h-100">
            <CustomerInsightsCard data={data} themeColors={themeColors} isDark={isDark} />
          </div>
        </div>

      </div>

      {/* ===== ROW 2: AGENT PERFORMANCE ===== */}
      <div className="dashboard-section-full">
        <div className="metric-card-premium full-width">
           <div className="premium-header">
            <div className="header-left">
              <div className="header-icon-glow">
                <Users size={24} />
              </div>
              <div>
                <h3 className="card-title-glow">Agent Performance</h3>
                <p className="card-subtitle">Real-time agent status & metrics</p>
              </div>
            </div>
          </div>
          <div className="card-body">
             <div className="metrics-grid-compact">
                <div className="metric-item">
                  <div className="metric-icon success">
                    <Users size={20} />
                  </div>
                  <div className="metric-content">
                    <div className="metric-value">{data?.agent_online || 0}</div>
                    <div className="metric-label">Active Agents</div>
                  </div>
                </div>
                <div className="metric-item">
                  <div className="metric-icon warning">
                    <Headphones size={20} />
                  </div>
                  <div className="metric-content">
                    <div className="metric-value">{data?.agent_ready || 0}</div>
                    <div className="metric-label">Agents Ready</div>
                  </div>
                </div>
                {/* <div className="metric-item">
                  <div className="metric-icon primary">
                    <Activity size={20} />
                  </div>
                  <div className="metric-content">
                    <div className="metric-value">{data?.agent_utilization || '85%'}</div>
                    <div className="metric-label">Utilization</div>
                  </div>
                </div> */}
                <div className="metric-item">
                  <div className="metric-icon info">
                    <Timer size={20} />
                  </div>
                  <div className="metric-content">
                    <div className="metric-value">{data?.avg_handle_time || '0s'}</div>
                    <div className="metric-label">Avg Handle Time</div>
                  </div>
                </div>
             </div>
          </div>
        </div>
      </div>

      {/* ===== MAIN DASHBOARD GRID - SPLIT INBOUND & OUTBOUND ===== */}
      <div className="dashboard-grid-split">
        
        {/* KOLOM INBOUND */}
        <div className="dashboard-column inbound-column">
          <div className="column-header">
            <h2 className="column-title p-5"><Phone size={20} /> Inbound Details</h2>
          </div>
          
          {/* ✅ Calls Per Hour with DateRangePicker */}
          <div className="metric-card-premium wide">
            <div className="metric-header">
              <div className="metric-title-section">
                <div className="metric-icon">
                  <LineChart size={22} />
                </div>
                <h3 className="metric-title">Calls Per Hour Analysis</h3>
              </div>
              {/* ✅ DateRangePicker */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginTop: '1rem' }}>
                {/* @ts-ignore - Library types don't include children prop but it works at runtime */}
                <DateRangePicker
                  initialSettings={{
                    startDate: callsPerHourDateRange[0] || new Date(),
                    endDate: callsPerHourDateRange[1] || new Date(),
                    ranges: {
                      'Today': [moment(), moment()],
                      'Yesterday': [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
                      'This Week': [moment().startOf('week'), moment().endOf('week')],
                      'Last Week': [moment().subtract(1, 'weeks').startOf('week'), moment().subtract(1, 'weeks').endOf('week')],
                      'This Month': [moment().startOf('month'), moment().endOf('month')],
                      'Last Month': [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')]
                    },
                    locale: {
                      format: 'DD MMM YYYY'
                    }
                  }}
                  onApply={(event, picker) => {
                    handleCallsPerHourDateChange([picker.startDate.toDate(), picker.endDate.toDate()]);
                  }}
                >
                  <button
                    style={{
                      padding: '0.5rem 1rem',
                      background: themeColors.bgSecondary,
                      color: themeColors.text,
                      border: `1px solid ${themeColors.border}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.borderColor = themeColors.primary}
                    onMouseLeave={(e) => e.currentTarget.style.borderColor = themeColors.border}
                  >
                    <Calendar size={16} />
                    {callsPerHourDateRange[0] ? formatDate(callsPerHourDateRange[0]) : 'Select Start'} - 
                    {callsPerHourDateRange[1] ? formatDate(callsPerHourDateRange[1]) : 'Select End'}
                  </button>
                </DateRangePicker>
              </div>
            </div>
            
            <div className="metric-body calls-per-hour-body">
              <CallsPerHour data={data} />
            </div>
          </div>

          {/* Abandoned Analysis */}
          <div className="metric-card-premium wide">
            <AbandonedAnalysis data={data} />
          </div>

          {/* Inbound Quick Stats */}
          <div className="metric-card-premium">
            <QuickStats data={data} type="inbound" />
          </div>
          
          {/* Inbound Activities */}
          <div className="metric-card-premium wide">
            <Activities data={data} type="inbound" />
          </div>
        </div>

        {/* KOLOM OUTBOUND */}
        <div className="dashboard-column outbound-column">
          <div className="column-header">
            <h2 className="column-title p-5"><Phone size={20} /> Outbound Details</h2>
          </div>

          {/* Outbound Quick Stats */}
          <div className="metric-card-premium">
            <QuickStats data={data} type="outbound" />
          </div>

          {/* Outbound Activities */}
          <div className="metric-card-premium wide">
            <Activities data={data} type="outbound" />
          </div>

          {/* Outbound Performance Stats */}
          {/* <div className="metric-card-premium wide">
            <StatisticsDashboard data={data} title="Outbound Performance" />
          </div> */}
        </div>
      </div>

      {/* System Status Bar */}
      <div className="system-status-bar-premium">
        <div className="status-items">
          <div className="status-item">
            <span className="status-label">Connection Type:</span>
            <span className={`status-value ${connectionRef.current.type}`}>
              {connectionRef.current.type.toUpperCase()}
            </span>
          </div>
          <div className="status-item">
            <span className="status-label">Last Data Sync:</span>
            <span className="status-value">{lastUpdate.toLocaleTimeString()}</span>
          </div>
          <div className="status-item">
            <span className="status-label">Auto Refresh:</span>
            <span className={`status-value ${autoRefresh ? 'enabled' : 'disabled'}`}>
              {autoRefresh ? 'ENABLED' : 'DISABLED'}
            </span>
          </div>
          <div className="status-item">
            <span className="status-label">Chart Date Range:</span>
            <span className="status-value">
              {callsPerHourDateRange[0] ? formatDate(callsPerHourDateRange[0]) : '-'} - 
              {callsPerHourDateRange[1] ? formatDate(callsPerHourDateRange[1]) : '-'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const DashboardAdmin: FC = () => {
  const intl = useIntl();
  return (
    <>
      <PageTitle breadcrumbs={[]}>{intl.formatMessage({ id: 'MENU.DASHBOARD' })}</PageTitle>
      <DashboardPage />
    </>
  );
};

export { DashboardAdmin };